import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  unitAssessmentAdminApi,
  type CreateUaRequest,
  type UpdateUaPlacementRequest,
  type UaSummary,
} from '@/api/unitAssessmentAdmin'
import { coachingKeys } from './useCoachingToday'
import { gradingDraftScope, useClearGradingDraftRow } from './useGradingDraft'

const validLearner = (id: number) => Number.isFinite(id) && id > 0

/** 단원평가 세트 카탈로그(학년-학기-단원). 학습자 무관 전역 캐시. */
export function useUnitAssessmentCatalog(includeInactive = false, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.unitAssessmentCatalog(includeInactive),
    queryFn: () => unitAssessmentAdminApi.getCatalog(includeInactive),
    enabled,
    staleTime: 5 * 60_000,
  })
}

/** 학습자(현 과정) 단원평가 특강 목록 — 트리 노드/이력. */
export function useUnitAssessments(learnerId: number, enabled = true) {
  return useQuery({
    queryKey: coachingKeys.unitAssessments(learnerId),
    queryFn: () => unitAssessmentAdminApi.list(learnerId),
    enabled: enabled && validLearner(learnerId),
    staleTime: 15_000,
  })
}

/** 단원평가 특강 채점 detail. */
export function useUnitAssessmentDetail(learnerId: number, uaId: number | null | undefined) {
  return useQuery({
    queryKey: coachingKeys.unitAssessmentDetail(learnerId, uaId ?? 0),
    queryFn: () => unitAssessmentAdminApi.getDetail(learnerId, uaId as number),
    enabled: validLearner(learnerId) && uaId != null && uaId > 0,
    staleTime: 0,
  })
}

export function useCreateUnitAssessment(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateUaRequest) => unitAssessmentAdminApi.create(learnerId, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) }),
  })
}

export function useUpdateUnitAssessmentPlacement(learnerId: number, uaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: UpdateUaPlacementRequest) =>
      unitAssessmentAdminApi.updatePlacement(learnerId, uaId, body),
    onSuccess: (summary) => {
      qc.setQueryData(
        coachingKeys.unitAssessments(learnerId),
        (rows: UaSummary[] | undefined) =>
          rows?.map((row) => row.id === summary.id ? summary : row),
      )
      qc.invalidateQueries({ queryKey: coachingKeys.unitAssessmentDetail(learnerId, uaId) })
      qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) })
    },
  })
}

export function useStartPrintUnitAssessment(learnerId: number, uaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => unitAssessmentAdminApi.startPrint(learnerId, uaId),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) })
      qc.invalidateQueries({ queryKey: coachingKeys.unitAssessmentDetail(learnerId, uaId) })
    },
  })
}

export interface GradeUnitAssessmentItemArgs {
  quizId: string
  answer?: string
  overrideIsCorrect?: boolean
}

export function useGradeUnitAssessmentItem(learnerId: number, uaId: number) {
  const qc = useQueryClient()
  const clearDraftRow = useClearGradingDraftRow()
  return useMutation({
    mutationFn: ({ quizId, answer, overrideIsCorrect }: GradeUnitAssessmentItemArgs) =>
      unitAssessmentAdminApi.gradePrintItem(learnerId, uaId, quizId, answer, overrideIsCorrect),
    onSuccess: (detail, variables) => {
      if (variables.answer != null) {
        clearDraftRow(gradingDraftScope.unitAssessment(learnerId, uaId), variables.quizId)
      }
      qc.setQueryData(coachingKeys.unitAssessmentDetail(learnerId, uaId), detail)
    },
  })
}

export function useCompleteUnitAssessment(learnerId: number, uaId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: () => unitAssessmentAdminApi.complete(learnerId, uaId),
    onSuccess: (detail) => {
      qc.setQueryData(coachingKeys.unitAssessmentDetail(learnerId, uaId), detail)
      qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) })
    },
  })
}

export function useCancelUnitAssessment(learnerId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (uaId: number) => unitAssessmentAdminApi.cancel(learnerId, uaId),
    onSuccess: () => qc.invalidateQueries({ queryKey: coachingKeys.unitAssessments(learnerId) }),
  })
}
